/**
 * Full Receipt popup — mirrors the Finance > Receipt page form including
 * the bill-wise allocation table. Lives in a Dialog so the Customer
 * Ledger popup can layer it on top without navigating away.
 *
 * Surfaces (call sites):
 *   • CustomerLedgerDialog → Receipt button (Invoice Management entry)
 *
 * The save flow is identical to ReceiptPage:
 *   POST /api/finance/receipt → POST /api/invoices/:id/apply-advance (xN)
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

export default function ReceiptWithAllocationsDialog({ open, onOpenChange, customer, defaultAmount, onSaved }) {
  const today = new Date().toISOString().split('T')[0];
  const [accounts, setAccounts] = useState([]);
  const [pendingBills, setPendingBills] = useState([]);
  const [loadingBills, setLoadingBills] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ date: today, account: '', amount: '', description: '' });
  const [allocations, setAllocations] = useState({});

  useEffect(() => {
    if (!open) return;
    // Pre-fill the Amount field with the caller-supplied due so the
    // staffer only has to confirm + pick a receiving account.
    const prefillAmt = defaultAmount && defaultAmount > 0 ? String(defaultAmount) : '';
    setForm({ date: today, account: '', amount: prefillAmt, description: '' });
    setAllocations({});
    api.get('/finance/accounts').then(r => {
      const accs = (r.data || []).filter(a => a.type === 'cash' || a.type === 'bank');
      setAccounts(accs);
    }).catch(() => setAccounts([]));
    if (customer?.id) {
      setLoadingBills(true);
      api.get(`/customers/${customer.id}/ledger`).then(r => {
        // The ledger gives us each invoice + its current balance. We only
        // need the open ones (balance > 0) for the allocation table.
        const opens = (r.data || []).filter(e => e.type === 'invoice' && (e.balance || 0) > 0).map(e => ({
          id: e.invoice_id, invoice_number: e.invoice_number, created_at: e.date,
          total: e.debit, balance_due: e.balance,
        }));
        setPendingBills(opens);
      }).catch(() => setPendingBills([])).finally(() => setLoadingBills(false));
    }
  }, [open, customer?.id]);

  const totalAllocated = Object.values(allocations).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const advanceLeftover = Math.max(0, (parseFloat(form.amount) || 0) - totalAllocated);

  const autoAllocate = () => {
    let remaining = parseFloat(form.amount) || 0;
    const next = {};
    for (const inv of pendingBills) {
      if (remaining <= 0) break;
      const take = Math.min(remaining, inv.balance_due);
      next[inv.id] = String(take);
      remaining -= take;
    }
    setAllocations(next);
  };

  const submit = async () => {
    const amt = parseFloat(form.amount);
    if (!form.customer_id && !customer?.id) { toast.error('Customer required'); return; }
    if (!form.account) { toast.error('Pick a receiving account'); return; }
    if (!amt || amt <= 0) { toast.error('Amount must be > 0'); return; }
    for (const inv of pendingBills) {
      const al = parseFloat(allocations[inv.id]) || 0;
      if (al > inv.balance_due + 0.01) {
        toast.error(`${inv.invoice_number}: allocation ${fmt(al)} exceeds open balance ${fmt(inv.balance_due)}`);
        return;
      }
    }
    if (totalAllocated > amt + 0.01) { toast.error(`Total allocated (${fmt(totalAllocated)}) exceeds receipt amount (${fmt(amt)})`); return; }

    setSubmitting(true);
    try {
      const res = await api.post('/finance/receipt', {
        customer_id: customer.id,
        amount: amt,
        payment_mode: form.account,
        date: form.date,
        description: form.description,
      });
      const voucherNo = res.data?.voucher_number || '';
      const applied = [];
      for (const inv of pendingBills) {
        const al = parseFloat(allocations[inv.id]) || 0;
        if (al <= 0) continue;
        try {
          await api.post(`/invoices/${inv.id}/apply-advance`, { amount: al });
          applied.push(inv.invoice_number);
        } catch (e) {
          toast.error(`Could not apply to ${inv.invoice_number}: ${e.response?.data?.detail || 'failed'}`);
        }
      }
      toast.success(applied.length
        ? `Receipt ${voucherNo} saved, applied to ${applied.length} bill(s)`
        : `Receipt ${voucherNo} recorded as advance`);
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save receipt');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl border-[#E4E4E7] max-w-3xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col" data-testid="receipt-with-allocations-dialog">
        <DialogHeader>
          <DialogTitle>New Receipt &mdash; {customer?.name}</DialogTitle>
          <DialogDescription>Records the amount as a customer advance. Split across open bills below; remainder stays as advance.</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-auto space-y-4 pr-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Date *</Label>
              <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-lg mt-1" data-testid="rwa-date" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Receiving Account *</Label>
              <Select value={form.account} onValueChange={v => setForm({ ...form, account: v })}>
                <SelectTrigger className="rounded-lg mt-1" data-testid="rwa-account"><SelectValue placeholder="Select account..." /></SelectTrigger>
                <SelectContent>{accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name} ({a.type})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Amount *</Label>
              <Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="0" className="rounded-lg mt-1 font-mono" data-testid="rwa-amount" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Description</Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional note" className="rounded-lg mt-1" data-testid="rwa-description" />
            </div>
          </div>

          <div className="rounded-lg border border-[#E4E4E7] p-3 bg-[#FAFAFA]">
            <div className="flex items-center justify-between mb-2 gap-2">
              <div>
                <p className="text-[11px] uppercase tracking-[0.15em] font-semibold text-[#52525B]">Pending Bills</p>
                <p className="text-[10px] text-[#A1A1AA] mt-0.5">Allocate across one or more open invoices. Remainder = advance.</p>
              </div>
              {pendingBills.length > 0 && (
                <Button type="button" variant="outline" size="sm" onClick={autoAllocate} className="rounded-lg h-7 text-[11px] border-[#3B82F6] text-[#3B82F6]" data-testid="rwa-auto-allocate">
                  Auto Fill (FIFO)
                </Button>
              )}
            </div>
            {loadingBills ? (
              <p className="text-xs text-[#A1A1AA] italic">Loading...</p>
            ) : pendingBills.length === 0 ? (
              <p className="text-xs text-[#A1A1AA] italic">No pending bills. Receipt will be parked as advance.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Bill #</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Date</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Total</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Open Balance</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right w-32">Apply</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingBills.map(inv => (
                    <TableRow key={inv.id} data-testid={`rwa-alloc-row-${inv.invoice_number}`}>
                      <TableCell className="text-xs font-mono text-[#3B82F6]">{inv.invoice_number}</TableCell>
                      <TableCell className="text-xs text-[#52525B]">{inv.created_at ? new Date(inv.created_at).toLocaleDateString('en-IN') : '-'}</TableCell>
                      <TableCell className="text-xs text-right font-mono">{fmt(inv.total)}</TableCell>
                      <TableCell className="text-xs text-right font-mono text-[#EF4444]">{fmt(inv.balance_due)}</TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number" min={0} step="0.01"
                          value={allocations[inv.id] ?? ''}
                          onChange={e => setAllocations({ ...allocations, [inv.id]: e.target.value })}
                          placeholder="0"
                          className="h-7 text-xs text-right font-mono rounded-md"
                          data-testid={`rwa-alloc-input-${inv.invoice_number}`}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <div className="flex items-center justify-end gap-4 text-xs mt-3 pt-2 border-t border-[#E4E4E7]">
              <span className="text-[#52525B]">Allocated: <span className="font-mono font-semibold text-[#0A0A0A]">{fmt(totalAllocated)}</span></span>
              <span className="text-[#52525B]">As Advance: <span className="font-mono font-semibold text-[#3B82F6]">{fmt(advanceLeftover)}</span></span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="rwa-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-[#10B981] text-white hover:bg-[#059669] hover:text-white rounded-xl" data-testid="rwa-save">
            {submitting ? 'Saving...' : 'Save Receipt'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
